import { CalendarCheckIcon, CheckCircleIcon } from '@phosphor-icons/react';
import { Button } from '~/components/ui/button';
import { useDayOneState } from '~/hooks/queries/use-day-one';
import {
  useApproveStarterCrons,
  useDeclineStarterCrons,
} from '~/hooks/mutations/use-day-one';
import { ConnectIntegrationCard } from './connect-integration-card';
import type { StarterCronProposalSignalData } from './tool-signals';

/**
 * FRONT-907: renders a `starter-cron-proposal` signal inline in the assistant
 * message — the day-one starter-automations card. Approve is a real,
 * multi-step backend exchange (2026-07-20 review): it asks which real mail/
 * calendar provider the user actually has (only when more than one genuinely
 * exists), gates real cron creation on that provider being genuinely
 * connected (reusing the same ConnectIntegrationCard + polling/Retry
 * mechanism used everywhere else), and only then creates the two crons.
 * "Not now" records the decline so the proposal stays discoverable on the
 * mirror surfaces (follow-up PR). The card reads the day-one state so a
 * reloaded page renders it resolved, not actionable.
 */
export function StarterCronProposalCard({ crons }: StarterCronProposalSignalData) {
  const { data: dayOne } = useDayOneState();
  const approve = useApproveStarterCrons();
  const decline = useDeclineStarterCrons();

  if (!crons?.length) return null;

  const sessionId = dayOne?.sessionId ?? null;
  const status = dayOne?.proposalStatus ?? 'pending';
  const busy = approve.isPending || decline.isPending;
  const step = approve.data?.status;

  return (
    <div className="border border-border rounded-lg bg-card max-w-[85%] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <CalendarCheckIcon size={18} className="shrink-0 text-muted-foreground" />
        <span className="text-base font-medium text-foreground">
          {status === 'accepted'
            ? 'Starter automations — set up'
            : status === 'declined'
              ? 'Starter automations — skipped for now'
              : 'Proposed automations — needs your OK'}
        </span>
        {status === 'accepted' && (
          <CheckCircleIcon size={18} className="shrink-0 text-success ml-auto" />
        )}
      </div>

      <ul className="px-4 py-3 space-y-3">
        {crons.map((cron) => (
          <li key={cron.name} className="text-base">
            <div className="flex items-baseline gap-2">
              <span className="font-semibold text-foreground">{cron.name}</span>
              <span className="text-muted-foreground tabular-nums text-sm">{cron.schedule}</span>
            </div>
            <p className="text-muted-foreground mt-0.5">{cron.description}</p>
          </li>
        ))}
      </ul>

      {status === 'pending' && step === 'needs-provider-choice' ? (
        <div className="px-4 py-3 border-t border-border">
          <span className="text-sm text-muted-foreground">
            Which do you use for mail and calendar?
          </span>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {approve.data?.candidates?.map((c) => (
              <Button
                key={c.providerKey}
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  sessionId && approve.mutate({ sessionId, providerKey: c.providerKey })
                }
              >
                {c.providerName}
              </Button>
            ))}
          </div>
        </div>
      ) : status === 'pending' && step === 'needs-connect' && approve.data?.connect ? (
        <div className="px-4 py-3 border-t border-border">
          <ConnectIntegrationCard
            providerKey={approve.data.connect.providerKey}
            providerName={approve.data.connect.providerName}
            flavour="connect"
            originalUserText="Set up my starter automations"
            onSendMessage={() => {
              const providerKey = approve.data?.connect?.providerKey;
              if (sessionId && providerKey) approve.mutate({ sessionId, providerKey });
            }}
          />
        </div>
      ) : status === 'pending' ? (
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-border">
          <span className="text-sm text-muted-foreground">
            Nothing runs until you approve. You can ask EDDY to change these anytime.
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="ghost"
              disabled={busy || !sessionId}
              onClick={() => sessionId && decline.mutate(sessionId)}
            >
              Not now
            </Button>
            <Button
              type="button"
              disabled={busy || !sessionId}
              onClick={() => sessionId && approve.mutate({ sessionId })}
            >
              {approve.isPending ? 'Setting up…' : 'Set these up'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-4 py-3 border-t border-border">
          <span className="text-sm text-muted-foreground">
            {status === 'accepted'
              ? 'Live on your Schedule page — ask EDDY anytime to change times or switch them off.'
              : 'No problem. You can always ask EDDY to set these up later.'}
          </span>
        </div>
      )}
    </div>
  );
}
